import { detectKeys, midiNoteKey, scalePitchClasses, type KeyCandidate, type MidiNote, type PitchFrame } from "./music.ts";

export type ConversionMode = "standard" | "key-assist";
export type KeyAssistStrength = "light" | "normal" | "strong";

export type KeySegment = {
  startBar: number;
  endBar: number;
  key: KeyCandidate;
  confidence: number;
  modulating: boolean;
};

export type KeyAssistResult = {
  notes: MidiNote[];
  segments: KeySegment[];
  changedNoteKeys: string[];
};

const STRENGTH_WEIGHT: Record<KeyAssistStrength, number> = { light: 0.18, normal: 0.34, strong: 0.5 };

function median(values: number[]): number {
  if (!values.length) return 0;
  const sorted = [...values].sort((a, b) => a - b);
  const middle = Math.floor(sorted.length / 2);
  return sorted.length % 2 ? sorted[middle] : (sorted[middle - 1] + sorted[middle]) / 2;
}

function keyId(candidate: KeyCandidate): string {
  return `${candidate.tonicIndex}:${candidate.mode}`;
}

function confidenceForWindow(notes: MidiNote[], candidates: KeyCandidate[]): number {
  if (!notes.length || candidates.length < 2) return 0;
  const evidenceBeats = notes.reduce((sum, note) => sum + note.durationBeats, 0);
  const evidence = Math.min(1, evidenceBeats / 4);
  const margin = Math.max(0, candidates[0].score - candidates[1].score);
  return Math.min(1, evidence * Math.min(1, margin / 0.18));
}

/**
 * Builds a stable, progressive key history. A new key must win two consecutive
 * windows before the current segment changes; the pending windows are marked as
 * modulating so note correction is weakened there.
 */
export function buildKeySegments(notes: MidiNote[], totalBars?: number): KeySegment[] {
  const bars = totalBars ?? Math.max(1, ...notes.map((note) => Math.floor(note.startBeat / 4) + 1));
  const segments: KeySegment[] = [];
  let stable: KeyCandidate | null = null;
  let pendingId: string | null = null;
  let pendingBars = 0;

  for (let bar = 0; bar < bars; bar += 1) {
    const windowStart = Math.max(0, bar - 3) * 4;
    const windowEnd = (bar + 1) * 4;
    const windowNotes = notes.filter((note) => note.startBeat >= windowStart && note.startBeat < windowEnd);
    const candidates = detectKeys(windowNotes);
    const candidate = candidates[0];
    const confidence = confidenceForWindow(windowNotes, candidates);
    let modulating = false;

    if (candidate && confidence >= 0.55) {
      if (!stable) {
        if (pendingId === keyId(candidate)) pendingBars += 1;
        else { pendingId = keyId(candidate); pendingBars = 1; }
        if (pendingBars >= 2) { stable = candidate; pendingId = null; pendingBars = 0; }
      } else if (keyId(candidate) !== keyId(stable)) {
        const stableKey = stable;
        const stableInWindow = candidates.find((item) => keyId(item) === keyId(stableKey));
        if (!stableInWindow || candidate.score - stableInWindow.score < 0.08) {
          pendingId = null;
          pendingBars = 0;
          const shownKey = stableKey;
          const previous = segments.at(-1);
          if (previous && keyId(previous.key) === keyId(shownKey) && previous.modulating === false && Math.abs(previous.confidence - confidence) < 0.15) {
            previous.endBar = bar;
            previous.confidence = Math.max(previous.confidence, confidence);
          } else {
            segments.push({ startBar: bar, endBar: bar, key: shownKey, confidence, modulating: false });
          }
          continue;
        }
        modulating = true;
        if (pendingId === keyId(candidate)) pendingBars += 1;
        else { pendingId = keyId(candidate); pendingBars = 1; }
        if (pendingBars >= 2) { stable = candidate; pendingId = null; pendingBars = 0; modulating = false; }
      } else {
        pendingId = null;
        pendingBars = 0;
      }
    } else {
      pendingId = null;
      pendingBars = 0;
    }

    const shownKey = stable ?? candidate;
    if (!shownKey) continue;
    const previous = segments.at(-1);
    if (previous && keyId(previous.key) === keyId(shownKey) && previous.modulating === modulating && Math.abs(previous.confidence - confidence) < 0.15) {
      previous.endBar = bar;
      previous.confidence = Math.max(previous.confidence, confidence);
    } else {
      segments.push({ startBar: bar, endBar: bar, key: shownKey, confidence, modulating: modulating || !stable });
    }
  }
  return segments;
}

function segmentAt(segments: KeySegment[], bar: number): KeySegment | undefined {
  return segments.find((segment) => bar >= segment.startBar && bar <= segment.endBar) ?? segments.at(-1);
}

export function keyConfidenceLabel(confidence: number): "低" | "中" | "高" {
  return confidence >= 0.78 ? "高" : confidence >= 0.55 ? "中" : "低";
}

/**
 * Applies a key prior only to already detected notes. It never adds/removes a
 * note and never changes timing or duration. Stable chromatic notes, low-key
 * confidence, modulation windows and manual edits remain untouched.
 */
export function applyKeyAssist(
  frames: PitchFrame[],
  standardNotes: MidiNote[],
  bpm: number,
  segments: KeySegment[],
  strength: KeyAssistStrength = "normal",
  manuallyEditedKeys: ReadonlySet<string> = new Set(),
  weightPercent?: number,
): KeyAssistResult {
  const secondsPerBeat = 60 / bpm;
  const changedNoteKeys: string[] = [];
  const notes = standardNotes.map((note) => {
    const originalKey = midiNoteKey(note);
    if (manuallyEditedKeys.has(originalKey)) return { ...note };
    const segment = segmentAt(segments, Math.floor(note.startBeat / 4));
    if (!segment || segment.confidence < 0.55 || segment.modulating) return { ...note };

    const start = note.startBeat * secondsPerBeat;
    const end = (note.startBeat + note.durationBeats) * secondsPerBeat;
    const evidence = frames.filter((frame) => frame.time >= start && frame.time < end && frame.midiFloat !== null && frame.confidence > 0);
    if (!evidence.length) return { ...note };
    const pitches = evidence.map((frame) => frame.midiFloat as number);
    const center = median(pitches);
    const stability = median(pitches.map((pitch) => Math.abs(pitch - center)));
    const pitchConfidence = evidence.reduce((sum, frame) => sum + frame.confidence, 0) / evidence.length;
    const distanceToStandard = Math.abs(center - note.midi);

    // A centered, confident or sustained chromatic pitch is musical evidence,
    // not an error to force into the estimated scale.
    const strongChromatic = distanceToStandard <= 0.24 && pitchConfidence >= 0.76 && (stability <= 0.16 || note.durationBeats >= 1);
    if (strongChromatic || distanceToStandard < 0.32 || distanceToStandard > 0.68) return { ...note };

    const scale = scalePitchClasses(segment.key.tonicIndex, segment.key.mode);
    if (scale.has(((note.midi % 12) + 12) % 12)) return { ...note };
    const alternatives = [note.midi - 1, note.midi + 1].filter((midi) => scale.has(((midi % 12) + 12) % 12));
    if (!alternatives.length) return { ...note };

    const configuredWeight = weightPercent === undefined ? STRENGTH_WEIGHT[strength] : Math.max(0, Math.min(0.75, weightPercent / 100));
    const effectiveWeight = configuredWeight * segment.confidence * Math.max(0.25, 1 - stability);
    const currentScore = 1 - Math.abs(center - note.midi);
    const best = alternatives
      .map((midi) => ({ midi, score: 1 - Math.abs(center - midi) + effectiveWeight }))
      .sort((a, b) => b.score - a.score)[0];
    if (!best || best.score <= currentScore + 0.03) return { ...note };
    changedNoteKeys.push(originalKey);
    return { ...note, midi: best.midi };
  });
  return { notes, segments, changedNoteKeys };
}

export function convertWithKeyAssist(
  mode: ConversionMode,
  frames: PitchFrame[],
  standardNotes: MidiNote[],
  bpm: number,
  strength: KeyAssistStrength,
  manuallyEditedKeys: ReadonlySet<string> = new Set(),
  weightPercent?: number,
): KeyAssistResult {
  const segments = buildKeySegments(standardNotes);
  if (mode === "standard") return { notes: standardNotes.map((note) => ({ ...note })), segments, changedNoteKeys: [] };
  return applyKeyAssist(frames, standardNotes, bpm, segments, strength, manuallyEditedKeys, weightPercent);
}
